import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import {
  fetchRiskPermissions,
  fetchRiskRecords,
  fetchRiskActionCards,
  fetchOrganisationEmployees,
  fetchMissionGuestData,
  fetchEstimatedRescueData,
} from "../../store/reducers/riskAnalysisSlice";
import { getOrganizationById } from "../../store/reducers/organizationSlice";
import {
  getLocalDateOrFallback,
  getLocalHourOrFallback,
} from "../utils/localDateTime";
import { RISK_CHART_COLORS } from "../../utils/actionCardColors";
import {
  MIN_DATE,
  T,
  showMissionGuest,
  showEstimated,
} from "../utils/constants";
import { todayStr, threeMonthsAgoStr, weekAgoStr } from "../utils/dateHelpers";
import {
  normalizePrototypeGroups,
  dedupeBy,
  hasValidEmployeeValue,
  getIncidentTypeValue,
  isTruthyFlag,
  buildFallbackCardsFromRecords,
  getRiskRecordKey,
  isMinInjuriesRecord,
  isMinInjuriesCard,
} from "../utils/riskRecordUtils";
import { downloadCsv } from "../utils/csvExport";
import { captureNodeAsPng } from "../utils/exportHelpers";
import { generateRiskAnalysisPdf } from "../utils/riskPdfExport";

const DEFAULT_TIME_RANGE = [0, 24];

const clampTimeRange = (range, allowedRange) => {
  const [allowedMin, allowedMax] = allowedRange;
  const fallbackRange =
    Array.isArray(range) && range.length >= 2 ? range : allowedRange;
  const nextMin = Math.max(
    allowedMin,
    Math.min(allowedMax, Number(fallbackRange[0]))
  );
  const nextMax = Math.max(
    allowedMin,
    Math.min(allowedMax, Number(fallbackRange[1]))
  );
  const normalizedMin = Number.isFinite(nextMin)
    ? Math.floor(nextMin)
    : allowedMin;
  const normalizedMax = Number.isFinite(nextMax)
    ? Math.ceil(nextMax)
    : allowedMax;

  return normalizedMin <= normalizedMax
    ? [normalizedMin, normalizedMax]
    : allowedRange;
};

export const useRiskAnalysisController = () => {
  const dispatch = useDispatch();
  const organizationId = Number(localStorage.getItem("organisation_id"));

  const {
    permissions: {
      data: permissionData,
      loading: permissionsLoading,
      error: permissionsError,
      success: permissionsSuccess,
    },
    riskRecords: {
      data: riskRecordsData,
      loading: riskRecordsLoading,
      error: riskRecordsError,
      success: riskRecordsSuccess,
    },
    actionCards: {
      data: actionCardsData,
      loading: actionCardsLoading,
      error: actionCardsError,
      success: actionCardsSuccess,
    },
    employees: {
      data: employeeData,
      loading: employeesLoading,
      error: employeesError,
      success: employeesSuccess,
    },
    missionGuest: {
      data: missionGuestData,
      loading: missionGuestLoading,
      error: missionGuestError,
      success: missionGuestSuccess,
    },
    estimatedRescue: {
      data: estimatedData,
      loading: estimatedLoading,
      error: estimatedError,
      success: estimatedSuccess,
    },
  } = useSelector((state) => state.riskAnalysis);

  const {
    organizationDetails,
    detailsLoading: orgLoading,
    detailsError: orgError,
  } = useSelector((state) => state.organization);

  const orgDetails = useMemo(
    () =>
      Array.isArray(organizationDetails)
        ? organizationDetails[0]
        : organizationDetails,
    [organizationDetails]
  );

  const [lang, setLang] = useState("dk");
  const [tab, setTab] = useState(0);
  const [timeRange, setTimeRange] = useState([0, 24]);
  const [dateFrom, setDateFrom] = useState(MIN_DATE);
  const [dateTo, setDateTo] = useState(todayStr());
  const [employees, setEmployees] = useState([]);
  const [setNumbers, setSetNumbers] = useState([]);
  const [showMinInjuries, setShowMinInjuries] = useState(true);
  const [showRealIncident, setShowRealIncident] = useState(true);
  const [showTraining, setShowTraining] = useState(false);
  const [showRead, setShowRead] = useState(false);
  const [showNull, setShowNull] = useState(false);
  const [showIncidentYellow, setShowIncidentYellow] = useState(false);
  const [showIncidentRed, setShowIncidentRed] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pngDownloading, setPngDownloading] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);

  // PreVent Data filters
  const [rDateFrom, setRDateFrom] = useState(threeMonthsAgoStr());
  const [rDateTo, setRDateTo] = useState(todayStr());
  const [rTimeRange, setRTimeRange] = useState([0, 24]);
  const [rEmployees, setREmployees] = useState([]);
  const [rSetNumbers, setRSetNumbers] = useState([]);
  const [rShowNearMiss, setRShowNearMiss] = useState(true);
  const [rShowPrevention, setRShowPrevention] = useState(true);
  const [rShowDoubtAboutReporting, setRShowDoubtAboutReporting] =
    useState(true);

  // Mission filters
  const [mDateFrom, setMDateFrom] = useState(weekAgoStr());
  const [mDateTo, setMDateTo] = useState(todayStr());
  const [mTimeRange, setMTimeRange] = useState([0, 24]);
  const [mSetNumbers, setMSetNumbers] = useState([]);
  const [mShowGreen, setMShowGreen] = useState(true);
  const [mShowYellow, setMShowYellow] = useState(true);
  const [mShowRed, setMShowRed] = useState(true);

  // Guest filters
  const [gDateFrom, setGDateFrom] = useState(weekAgoStr());
  const [gDateTo, setGDateTo] = useState(todayStr());
  const [gTimeRange, setGTimeRange] = useState([0, 24]);
  const [gSetNumbers, setGSetNumbers] = useState([]);

  // Estimated filters
  const [eDateFrom, setEDateFrom] = useState(MIN_DATE);
  const [eDateTo, setEDateTo] = useState(todayStr());
  const [eTimeRange, setETimeRange] = useState([0, 24]);
  const [eSetNumbers, setESetNumbers] = useState([]);
  const [eBeach, setEBeach] = useState("");

  const incidentRef = useRef(null);
  const tPerformanceRef = useRef(null);
  const tableRef = useRef(null);
  const missionRef = useRef(null);
  const guestRef = useRef(null);
  const estimatedRef = useRef(null);
  const incidentChartRefs = useRef({});
  const tPerformanceChartRefs = useRef({});
  const incidentImageExportRefs = useRef({});
  const incidentReportingImageExportRefs = useRef({});
  const tPerformanceImageExportRefs = useRef({});
  const incidentExportRefs = useRef({});
  const incidentReportingExportRefs = useRef({});
  const tPerformanceExportRefs = useRef({});
  const missionExportRef = useRef(null);
  const guestExportRef = useRef(null);
  const estimatedExportRef = useRef(null);

  const assignNodeRef = useCallback((refStore, key, node) => {
    if (!key) return;
    if (node) {
      refStore.current[key] = node;
      return;
    }
    delete refStore.current[key];
  }, []);

  useEffect(() => {
    if (!organizationId) return;
    dispatch(fetchRiskPermissions(organizationId));
    dispatch(fetchRiskRecords(organizationId));
    dispatch(fetchRiskActionCards(organizationId));
    dispatch(fetchOrganisationEmployees(organizationId));
    dispatch(getOrganizationById(organizationId));
  }, [dispatch, organizationId]);

  useEffect(() => {
    if (!organizationId || !showMissionGuest(organizationId)) return;
    dispatch(
      fetchMissionGuestData({
        organizationId,
        startDate: mDateFrom || weekAgoStr(),
        endDate: mDateTo || todayStr(),
      })
    );
  }, [dispatch, organizationId, mDateFrom, mDateTo]);

  // Guest filters should also trigger a refresh
  useEffect(() => {
    if (!organizationId || !showMissionGuest(organizationId)) return;
    dispatch(
      fetchMissionGuestData({
        organizationId,
        startDate: gDateFrom || weekAgoStr(),
        endDate: gDateTo || todayStr(),
      })
    );
  }, [dispatch, organizationId, gDateFrom, gDateTo]);

  useEffect(() => {
    if (!showEstimated(organizationId)) return;
    if (organizationId === 19) {
      dispatch(fetchEstimatedRescueData(organizationId));
    }
  }, [dispatch, organizationId]);

  const prototypeRiskGroups = useMemo(
    () => normalizePrototypeGroups(riskRecordsData, "items"),
    [riskRecordsData]
  );
  const prototypeActionCardGroups = useMemo(
    () => normalizePrototypeGroups(actionCardsData, "cards"),
    [actionCardsData]
  );
  const isPrototypeGrouped = useMemo(
    () =>
      Boolean(
        riskRecordsData &&
          !Array.isArray(riskRecordsData) &&
          typeof riskRecordsData === "object"
      ),
    [riskRecordsData]
  );
  const excludedCardIds = useMemo(() => {
    if (organizationId === 2) return ["170"];
    if (organizationId === 19)
      return ["161", "162" ,"163", "164", "165", "166", "167", "168", "169", "171", "170", "79"];
    return [];
  }, [organizationId]);

  const rawRecords = useMemo(
    () =>
      dedupeBy(
        prototypeRiskGroups.flatMap((group) => group.items ?? []),
        (record) => getRiskRecordKey(record)
      ).filter((record) => {
        const cardId = String(record?.action_card_number ?? record?.ac_no ?? record?.id ?? "");
        return !excludedCardIds.includes(cardId);
      }),
    [prototypeRiskGroups, excludedCardIds]
  );
  const actionCards = useMemo(() => {
    const deduped = dedupeBy(
      prototypeActionCardGroups.flatMap((group) => group.cards ?? []),
      (card) => String(card?.ac_no ?? card?.id ?? "")
    ).filter((card) => {
      const cardId = String(card?.ac_no ?? card?.id ?? "");
      return !excludedCardIds.includes(cardId);
    });
    return deduped.length ? deduped : buildFallbackCardsFromRecords(rawRecords);
  }, [prototypeActionCardGroups, rawRecords, excludedCardIds]);
  const prototypeActionCardMap = useMemo(() => {
    const map = {};
    prototypeActionCardGroups.forEach((group) => {
      const cards = Array.isArray(group.cards) ? group.cards : [];
      if (group.key) {
        map[group.key] = cards;
      }
      if (group.prototype_set_id) {
        map[group.prototype_set_id] = cards;
      }
    });
    return map;
  }, [prototypeActionCardGroups]);
  const employeeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(employeeData) ? employeeData : [])
            .map((employee) => employee?.employee_id)
            .filter(Boolean)
        )
      ).sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true })
      ),
    [employeeData]
  );
  const selectedEmployeeUserIds = useMemo(
    () =>
      (Array.isArray(employeeData) ? employeeData : [])
        .filter((employee) => employees.includes(employee?.employee_id))
        .map((employee) => String(employee?.user_id ?? "")),
    [employeeData, employees]
  );
  const realIncidentSetOptions = useMemo(() => {
    const uniq = new Set(
      rawRecords
        .map((record) => String(record?.nb_set ?? ""))
        .filter((value) => value.trim() !== "")
    );
    return Array.from(uniq).sort((a, b) => Number(a) - Number(b));
  }, [rawRecords]);

  const permissions = permissionData?.permissions_list ?? [];
  const userRole = (permissionData?.user_role ?? "").toLowerCase();
  const currentUserId = String(permissionData?.user_id ?? "");
  const canDate = permissions.includes("use_date_filter");
  const canTime = permissions.includes("use_time_a_day_filter");
  const canSet = permissions.includes("use_set_number_filter");
  const canEmployee = permissions.includes("use_employee_name_filter");
  const canDownload = permissions.includes("download_csv");
  const showReportingCategory = Boolean(orgDetails?.show_reporting_category);
  const allowedTimeRange = DEFAULT_TIME_RANGE;

  useEffect(() => {
    setTimeRange((current) => clampTimeRange(current, allowedTimeRange));
    setRTimeRange((current) => clampTimeRange(current, allowedTimeRange));
    setMTimeRange((current) => clampTimeRange(current, allowedTimeRange));
    setGTimeRange((current) => clampTimeRange(current, allowedTimeRange));
    setETimeRange((current) => clampTimeRange(current, allowedTimeRange));
  }, [allowedTimeRange]);

  const applyBaseFilter = useCallback(
    (pool) =>
      pool.filter((rec) => {
        const recDate = getLocalDateOrFallback(rec.utc_tid_skannet);
        if (dateFrom && recDate < dateFrom) return false;
        if (dateTo && recDate > dateTo) return false;
        if (
          employees.length > 0 &&
          !employees.includes(rec.employee) &&
          !selectedEmployeeUserIds.includes(String(rec.user_id ?? ""))
        )
          return false;
        if (setNumbers.length > 0 && !setNumbers.includes(String(rec.nb_set)))
          return false;
        const hour = getLocalHourOrFallback(rec.utc_tid_skannet);
        if (hour !== null && (hour < timeRange[0] || hour > timeRange[1]))
          return false;
        return true;
      }),
    [
      dateFrom,
      dateTo,
      employees,
      selectedEmployeeUserIds,
      setNumbers,
      timeRange,
    ]
  );

  const applyPerformanceFilter = useCallback(
    (pool) =>
      pool.filter((rec) => {
        const recDate = getLocalDateOrFallback(rec.utc_tid_skannet);
        if (dateFrom && recDate < dateFrom) return false;
        if (dateTo && recDate > dateTo) return false;
        const hour = getLocalHourOrFallback(rec.utc_tid_skannet);
        if (hour !== null && (hour < timeRange[0] || hour > timeRange[1]))
          return false;
        return true;
      }),
    [dateFrom, dateTo, timeRange]
  );

  const applyReportingBaseFilter = useCallback(
    (pool) =>
      pool.filter((rec) => {
        const recDate = getLocalDateOrFallback(rec.utc_tid_skannet);
        if (rDateFrom && recDate < rDateFrom) return false;
        if (rDateTo && recDate > rDateTo) return false;
        if (
          rEmployees.length > 0 &&
          !rEmployees.includes(rec.employee) &&
          !selectedEmployeeUserIds.includes(String(rec.user_id ?? ""))
        ) {
          return false;
        }
        if (
          rSetNumbers.length > 0 &&
          !rSetNumbers.includes(String(rec.nb_set))
        ) {
          return false;
        }
        const hour = getLocalHourOrFallback(rec.utc_tid_skannet);
        if (hour !== null && (hour < rTimeRange[0] || hour > rTimeRange[1]))
          return false;
        return true;
      }),
    [
      rDateFrom,
      rDateTo,
      rEmployees,
      selectedEmployeeUserIds,
      rSetNumbers,
      rTimeRange,
    ]
  );

  const getPrototypeTitle = useCallback(
    (group, isPerformanceTab = false) => {
      if (!group) return "";
      
      const pId = group.prototype_set_id || group.key || "";
      
      if (pId === "FA" || pId === "First Aid") {
        if (isPerformanceTab) {
          return lang === "dk" 
            ? "Action-cards® til sikker førstehjælp (kun træningsdata)" 
            : "Action-cards® for confident first aid (training data only)";
        }
        return lang === "dk" 
          ? "Action-cards® til sikker førstehjælp" 
          : "Action-cards® for confident first aid";
      }
      
      if (pId === "VE" || pId === "Emergency Service" || pId === "C01") {
        if (isPerformanceTab) {
          return lang === "dk" 
            ? "Action-cards® til robust beredskab (kun træningsdata)" 
            : "Action-cards® for Vital Emergency response (training data only)";
        }
        return lang === "dk" 
          ? "Action-cards® til robust beredskab" 
          : "Action-cards® for Vital Emergency response";
      }

      const setName =
        lang === "dk"
          ? group.danish_set_name || group.english_set_name
          : group.english_set_name || group.danish_set_name;
      return [pId, setName].filter(Boolean).join(" - ");
    },
    [lang]
  );
  const sanitizeFilePart = useCallback((value) => {
    const normalized = String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return normalized || "default";
  }, []);

  const buildImageFileName = useCallback(
    (prefix, group) => {
      if (!isPrototypeGrouped) return `${prefix}.png`;
      const suffix = sanitizeFilePart(
        group?.prototype_set_id || group?.key || group?.title || "default"
      );
      return `${prefix}-${suffix}.png`;
    },
    [isPrototypeGrouped, sanitizeFilePart]
  );

  const filterNullEmployeeRows = useCallback(
    (records) =>
      showNull
        ? records
        : records.filter((record) => hasValidEmployeeValue(record?.employee)),
    [showNull]
  );

  const isDefaultRecord = useCallback(
    (record) =>
      Number(record?.training_purpose ?? 0) === 1 &&
      Number(record?.has_read ?? 0) === 0,
    []
  );

  const isTrainingRecord = useCallback(
    (record) => Number(record?.training_purpose ?? 0) === 0,
    []
  );

  const isReadRecord = useCallback(
    (record) =>
      Number(record?.training_purpose ?? 0) === 1 &&
      Number(record?.has_read ?? 0) === 1,
    []
  );

  const hasReportingCategoryFlag = useCallback(
    (record) =>
      isTruthyFlag(record?.has_never_miss) ||
      isTruthyFlag(record?.has_prevention) ||
      isTruthyFlag(record?.has_doubt_about_reporting),
    []
  );

  const isReportingCategoryRecord = useCallback(
    (record) => isDefaultRecord(record) && hasReportingCategoryFlag(record),
    [hasReportingCategoryFlag, isDefaultRecord]
  );

  const isRealIncidentRecord = useCallback(
    (record) =>
      isDefaultRecord(record) &&
      (!orgDetails?.show_reporting_category ||
        !hasReportingCategoryFlag(record)),
    [hasReportingCategoryFlag, isDefaultRecord, orgDetails]
  );

  const defaultPool = useMemo(
    () =>
      applyBaseFilter(
        rawRecords.filter((record) => isRealIncidentRecord(record))
      ),
    [applyBaseFilter, rawRecords, isRealIncidentRecord]
  );

  const trainingPool = useMemo(
    () =>
      applyBaseFilter(
        filterNullEmployeeRows(
          rawRecords.filter((record) => isTrainingRecord(record))
        )
      ),
    [applyBaseFilter, filterNullEmployeeRows, rawRecords, isTrainingRecord]
  );

  const readPool = useMemo(
    () =>
      applyBaseFilter(
        filterNullEmployeeRows(
          rawRecords.filter((record) => isReadRecord(record))
        )
      ),
    [applyBaseFilter, filterNullEmployeeRows, rawRecords, isReadRecord]
  );

  const prototypeChartGroups = useMemo(
    () =>
      prototypeRiskGroups.map((group) => {
        const groupRecords = dedupeBy(
          Array.isArray(group.items) ? group.items : [],
          (record) => getRiskRecordKey(record)
        );
        const defaultRecords = applyBaseFilter(
          groupRecords.filter((record) => isRealIncidentRecord(record))
        );
        const trainingRecords = applyBaseFilter(
          filterNullEmployeeRows(
            groupRecords.filter((record) => isTrainingRecord(record))
          )
        );
        const readRecords = applyBaseFilter(
          filterNullEmployeeRows(
            groupRecords.filter((record) => isReadRecord(record))
          )
        );
        const reportingCategoryRecords = showReportingCategory
          ? applyBaseFilter(
              groupRecords.filter((record) => isReportingCategoryRecord(record))
            )
          : [];
        const performanceRecords = applyPerformanceFilter(
          groupRecords.filter((record) => {
            const yes = Number(record.num_yes ?? 0);
            const no = Number(record.num_no ?? 0);
            const na = Number(record.num_na ?? 0);
            return isTrainingRecord(record) && yes > -1 && no > -1 && na > -1;
          })
        );
        const mappedCards =
          prototypeActionCardMap[group.key] ||
          prototypeActionCardMap[group.prototype_set_id] ||
          [];
        const groupActionCards = dedupeBy(
          mappedCards,
          (card) => String(card?.ac_no ?? card?.id ?? "")
        ).filter((card) => {
          const cardId = String(card?.ac_no ?? card?.id ?? "");
          return !excludedCardIds.includes(cardId);
        });

        return {
          ...group,
          title: getPrototypeTitle(group),
          performanceTitle: getPrototypeTitle(group, true),
          actionCards: groupActionCards,
          defaultRecords,
          trainingRecords,
          readRecords,
          reportingCategoryRecords,
          performanceRecords,
        };
      }),
    [
      applyBaseFilter,
      applyPerformanceFilter,
      filterNullEmployeeRows,
      getPrototypeTitle,
      isRealIncidentRecord,
      isReportingCategoryRecord,
      isReadRecord,
      isTrainingRecord,
      prototypeActionCardMap,
      prototypeRiskGroups,
      showReportingCategory,
      excludedCardIds,
    ]
  );

  const incidentChartGroups = useMemo(
    () =>
      showMinInjuries
        ? prototypeChartGroups
        : prototypeChartGroups.map((group) => ({
            ...group,
            actionCards: group.actionCards.filter(
              (card) => !isMinInjuriesCard(card)
            ),
            defaultRecords: group.defaultRecords.filter(
              (record) => !isMinInjuriesRecord(record)
            ),
            trainingRecords: group.trainingRecords.filter(
              (record) => !isMinInjuriesRecord(record)
            ),
            readRecords: group.readRecords.filter(
              (record) => !isMinInjuriesRecord(record)
            ),
            reportingCategoryRecords: group.reportingCategoryRecords.filter(
              (record) => !isMinInjuriesRecord(record)
            ),
          })),
    [prototypeChartGroups, showMinInjuries]
  );

  const includeTrainingData = showTraining || showNull;
  const includeReadData = showRead || showNull;

  const tableRecords = useMemo(() => {
    const hasIncidentTypeTableFilter = showIncidentYellow || showIncidentRed;

    return applyBaseFilter(
      rawRecords.filter((record) => {
        if (!showMinInjuries && isMinInjuriesRecord(record)) {
          return false;
        }

        if (isDefaultRecord(record)) {
          if (hasIncidentTypeTableFilter && isRealIncidentRecord(record)) {
            if (!showRealIncident) {
              return false;
            }

            const incidentType = getIncidentTypeValue(record);
            return (
              (showIncidentYellow && incidentType === "yellow") ||
              (showIncidentRed && incidentType === "red")
            );
          }

          if (showRealIncident) {
            return isRealIncidentRecord(record);
          }

          return false;
        }

        if (includeTrainingData && isTrainingRecord(record)) {
          return showNull || hasValidEmployeeValue(record?.employee);
        }
        if (includeReadData && isReadRecord(record)) {
          return showNull || hasValidEmployeeValue(record?.employee);
        }
        return false;
      })
    );
  }, [
    applyBaseFilter,
    rawRecords,
    orgDetails,
    isDefaultRecord,
    isRealIncidentRecord,
    showRealIncident,
    showIncidentYellow,
    showIncidentRed,
    includeTrainingData,
    isTrainingRecord,
    showNull,
    includeReadData,
    isReadRecord,
    showMinInjuries,
  ]);

  const reportingCategorySetOptions = useMemo(() => {
    const uniq = new Set(
      rawRecords
        .filter((record) => isReportingCategoryRecord(record))
        .map((record) => String(record?.nb_set ?? ""))
        .filter((value) => value.trim() !== "")
    );
    return Array.from(uniq).sort((a, b) => Number(a) - Number(b));
  }, [rawRecords, isReportingCategoryRecord]);

  const reportingCategoryBasePool = useMemo(
    () =>
      showReportingCategory
        ? applyReportingBaseFilter(
            rawRecords.filter((record) => isReportingCategoryRecord(record))
          )
        : [],
    [
      applyReportingBaseFilter,
      isReportingCategoryRecord,
      rawRecords,
      showReportingCategory,
    ]
  );

  const riskReportingCategoryBasePool = useMemo(
    () =>
      showReportingCategory
        ? applyBaseFilter(
            rawRecords.filter((record) => isReportingCategoryRecord(record))
          )
        : [],
    [
      applyBaseFilter,
      isReportingCategoryRecord,
      rawRecords,
      showReportingCategory,
    ]
  );

  const riskReportingTableRecords = useMemo(() => {
    return riskReportingCategoryBasePool.filter(
      (record) => showMinInjuries || !isMinInjuriesRecord(record)
    );
  }, [riskReportingCategoryBasePool, showMinInjuries]);

  const reportingTableRecords = useMemo(() => {
    const hasSelectedCategory =
      rShowNearMiss || rShowPrevention || rShowDoubtAboutReporting;

    if (!hasSelectedCategory) {
      return [];
    }

    return reportingCategoryBasePool.filter(
      (record) =>
        (rShowNearMiss && isTruthyFlag(record?.has_never_miss)) ||
        (rShowPrevention && isTruthyFlag(record?.has_prevention)) ||
        (rShowDoubtAboutReporting &&
          isTruthyFlag(record?.has_doubt_about_reporting))
    );
  }, [
    reportingCategoryBasePool,
    rShowNearMiss,
    rShowPrevention,
    rShowDoubtAboutReporting,
  ]);

  const reportingPrototypeGroups = useMemo(
    () =>
      prototypeRiskGroups.map((group) => {
        const groupRecords = dedupeBy(
          Array.isArray(group.items) ? group.items : [],
          (record) => getRiskRecordKey(record)
        );
        const reportingCategoryRecords = showReportingCategory
          ? applyReportingBaseFilter(
              groupRecords.filter((record) => isReportingCategoryRecord(record))
            )
          : [];
        const mappedCards =
          prototypeActionCardMap[group.key] ||
          prototypeActionCardMap[group.prototype_set_id] ||
          [];
        const fallbackCards = buildFallbackCardsFromRecords(groupRecords);
        const groupActionCards = dedupeBy(
          [...mappedCards, ...fallbackCards],
          (card) => String(card?.ac_no ?? card?.id ?? "")
        ).filter((card) => {
          const cardId = String(card?.ac_no ?? card?.id ?? "");
          return !excludedCardIds.includes(cardId);
        });

        return {
          ...group,
          title: getPrototypeTitle(group),
          actionCards: groupActionCards,
          reportingCategoryRecords,
        };
      }),
    [
      applyReportingBaseFilter,
      getPrototypeTitle,
      isReportingCategoryRecord,
      prototypeActionCardMap,
      prototypeRiskGroups,
      showReportingCategory,
      excludedCardIds,
    ]
  );

  // Per-dataset set options
  const missionSetOptions = useMemo(() => {
    const source = missionGuestData?.length ? missionGuestData : [];
    const uniq = new Set(
      source.map((r) => String(r.nb_set ?? "")).filter((v) => v.trim() !== "")
    );
    return Array.from(uniq).sort((a, b) => Number(a) - Number(b));
  }, [missionGuestData]);

  const estimatedSetOptions = useMemo(() => {
    const source = estimatedData?.length ? estimatedData : [];
    const uniq = new Set(
      source.map((r) => String(r.nb_set ?? "")).filter((v) => v.trim() !== "")
    );
    return Array.from(uniq).sort((a, b) => Number(a) - Number(b));
  }, [estimatedData]);

  const missionFiltered = useMemo(
    () =>
      (missionGuestData?.length ? missionGuestData : []).filter((r) => {
        const d = getLocalDateOrFallback(r.utc_scanned);
        if (mDateFrom && d < mDateFrom) return false;
        if (mDateTo && d > mDateTo) return false;
        if (mSetNumbers.length > 0 && !mSetNumbers.includes(String(r.nb_set)))
          return false;
        const hour = getLocalHourOrFallback(r.utc_scanned);
        if (hour !== null && (hour < mTimeRange[0] || hour > mTimeRange[1]))
          return false;
        return true;
      }),
    [missionGuestData, mDateFrom, mDateTo, mSetNumbers, mTimeRange]
  );

  const guestFiltered = useMemo(
    () =>
      (missionGuestData?.length ? missionGuestData : []).filter((r) => {
        const d = getLocalDateOrFallback(r.utc_scanned);
        if (gDateFrom && d < gDateFrom) return false;
        if (gDateTo && d > gDateTo) return false;
        if (gSetNumbers.length > 0 && !gSetNumbers.includes(String(r.nb_set)))
          return false;
        const hour = getLocalHourOrFallback(r.utc_scanned);
        if (hour !== null && (hour < gTimeRange[0] || hour > gTimeRange[1]))
          return false;
        return true;
      }),
    [missionGuestData, gDateFrom, gDateTo, gSetNumbers, gTimeRange]
  );

  const estimatedFiltered = useMemo(
    () =>
      (estimatedData?.length ? estimatedData : []).filter((r) => {
        const d = getLocalDateOrFallback(r.utc_scanned, r.date);
        if (eDateFrom && d < eDateFrom) return false;
        if (eDateTo && d > eDateTo) return false;
        if (eSetNumbers.length > 0 && !eSetNumbers.includes(String(r.nb_set)))
          return false;
        if (eBeach && r.beach !== eBeach) return false;
        const hour = getLocalHourOrFallback(r.utc_scanned);
        if (hour !== null && (hour < eTimeRange[0] || hour > eTimeRange[1]))
          return false;
        return true;
      }),
    [estimatedData, eDateFrom, eDateTo, eSetNumbers, eBeach, eTimeRange]
  );

  const t = (k) => T[lang][k];
  //TODO: update condition for DNK  || organizationId === 2
  const isGentofteLife = Number(organizationId) === 19;
  const hasIncidentTypeFilter = showIncidentYellow || showIncidentRed;
  const hasRiskReportingCategoryFilter =
    showReportingCategory && riskReportingTableRecords.length > 0;

  const orgName = (orgDetails && orgDetails.name) || "Organisation Name";

  const formatFilterValue = useCallback(
    (values) =>
      Array.isArray(values) && values.length ? values.join(", ") : "All",
    []
  );

  const buildImageHeaderRows = useCallback(
    () => [
      { label: "DOWNLOAD DATE", value: todayStr() },
      { label: "ORGANIZATION", value: orgName },
    ],
    [orgName]
  );

  const buildRiskImageDetailRows = useCallback(() => {
    const rows = [
      {
        label: "Date span",
        value: `${dateFrom || MIN_DATE} To ${dateTo || todayStr()}`,
      },
      { label: "Time span", value: `${timeRange[0]} To ${timeRange[1]}` },
      { label: "Set of Action-cards®", value: formatFilterValue(setNumbers) },
      { label: "Employees", value: formatFilterValue(employees) },
      { label: "Min. injuries", value: showMinInjuries ? "Yes" : "No" },
      {
        label: lang === "dk" ? "Faktisk hændelse" : "Real Incident",
        value: showRealIncident ? "Yes" : "No",
      },
    ];

    if (isGentofteLife && hasIncidentTypeFilter) {
      rows.push({ label: "Yellow", value: showIncidentYellow ? "Yes" : "No" });
      rows.push({ label: "Red", value: showIncidentRed ? "Yes" : "No" });
    }

    rows.push({ label: "Training", value: includeTrainingData ? "Yes" : "No" });
    rows.push({ label: "Read", value: includeReadData ? "Yes" : "No" });
    rows.push({ label: "Null Data Checked", value: showNull ? "Yes" : "No" });
    return rows;
  }, [
    dateFrom,
    dateTo,
    timeRange,
    formatFilterValue,
    setNumbers,
    employees,
    showMinInjuries,
    showRealIncident,
    isGentofteLife,
    showIncidentYellow,
    showIncidentRed,
    includeTrainingData,
    includeReadData,
    showNull,
  ]);

  const buildReportingImageDetailRows = useCallback(
    () => [
      {
        label: "Date span",
        value: `${dateFrom || MIN_DATE} To ${dateTo || todayStr()}`,
      },
      { label: "Time span", value: `${timeRange[0]} To ${timeRange[1]}` },
      { label: "Set of Action-cards®", value: formatFilterValue(setNumbers) },
      { label: "Employees", value: formatFilterValue(employees) },
    ],
    [dateFrom, dateTo, timeRange, formatFilterValue, setNumbers, employees]
  );

  const buildPerformanceImageDetailRows = useCallback(
    () => [
      {
        label: "Date span",
        value: `${dateFrom || MIN_DATE} To ${dateTo || todayStr()}`,
      },
      { label: "Time span", value: `${timeRange[0]} To ${timeRange[1]}` },
      { label: "Set of Action-cards®", value: "All" },
      { label: "Employees", value: "All" },
      { label: "Training", value: "No" },
      { label: "Read", value: "No" },
      { label: "Null Data Checked", value: "No" },
    ],
    [dateFrom, dateTo, timeRange]
  );

  const buildRiskPdfSummaryRows = useCallback(
    (defaultRecords, trainingRecords, readRecords) => {
      const rows = [];
      const showIncidentTypeBars =
        showRealIncident && isGentofteLife && hasIncidentTypeFilter;
      const showDefaultBar = showRealIncident;
      const showZeroDefaultBar =
        !showDefaultBar &&
        !showIncidentTypeBars &&
        !includeTrainingData &&
        !includeReadData;
      const yellowTotal = defaultRecords.reduce((sum, record) => {
        if (getIncidentTypeValue(record) !== "yellow") {
          return sum;
        }
        const count = Number(record?.real_incident_type_count);
        return sum + (Number.isFinite(count) && count > 0 ? count : 1);
      }, 0);
      const redTotal = defaultRecords.reduce((sum, record) => {
        if (getIncidentTypeValue(record) !== "red") {
          return sum;
        }
        const count = Number(record?.real_incident_type_count);
        return sum + (Number.isFinite(count) && count > 0 ? count : 1);
      }, 0);

      if (showIncidentTypeBars) {
        if (showIncidentYellow) {
          rows.push({
            label: lang === "dk" ? "Gul" : "Yellow",
            value: yellowTotal,
            color: RISK_CHART_COLORS.yellow,
            show: true,
          });
        }
        if (showIncidentRed) {
          rows.push({
            label: lang === "dk" ? "Rød" : "Red",
            value: redTotal,
            color: RISK_CHART_COLORS.red,
            show: true,
          });
        }
      }
      if (showDefaultBar || showZeroDefaultBar) {
        rows.push({
          label: lang === "dk" ? "Faktisk hændelse" : "Real Incident",
          value: showDefaultBar ? defaultRecords.length : 0,
          color: "var(--org-bg-color, #f6615f)",
          show: true,
        });
      }
      if (includeTrainingData) {
        rows.push({
          label: lang === "dk" ? "Træning" : "Training",
          value: trainingRecords.length,
          color: RISK_CHART_COLORS.training,
          show: true,
        });
      }
      if (includeReadData) {
        rows.push({
          label: lang === "dk" ? "Læsning" : "Reading",
          value: readRecords.length,
          color: RISK_CHART_COLORS.reading,
          show: true,
        });
      }
      return rows;
    },
    [
      hasIncidentTypeFilter,
      showRealIncident,
      isGentofteLife,
      lang,
      showIncidentYellow,
      showIncidentRed,
      includeReadData,
      includeTrainingData,
    ]
  );

  const buildReportingPdfSummaryRows = useCallback(
    (records) => {
      const rows = [];

      rows.push({
        label: lang === "dk" ? "Near miss" : "Near miss",
        value: records.reduce(
          (sum, record) => sum + (isTruthyFlag(record?.has_never_miss) ? 1 : 0),
          0
        ),
        color: RISK_CHART_COLORS.nearMiss,
        show: true,
      });

      rows.push({
        label: lang === "dk" ? "Forebyggelse" : "Prevention",
        value: records.reduce(
          (sum, record) => sum + (isTruthyFlag(record?.has_prevention) ? 1 : 0),
          0
        ),
        color: RISK_CHART_COLORS.prevention,
        show: true,
      });

      rows.push({
        label:
          lang === "dk" ? "Tvivl om rapportering" : "Doubt about reporting",
        value: records.reduce(
          (sum, record) =>
            sum + (isTruthyFlag(record?.has_doubt_about_reporting) ? 1 : 0),
          0
        ),
        color: RISK_CHART_COLORS.doubt,
        show: true,
      });

      return rows;
    },
    [lang]
  );

  const buildPerformancePdfSummaryRows = useCallback(
    (records, cards) => [
      {
        label: lang === "dk" ? "Kort" : "Cards",
        value: cards.length,
        color: RISK_CHART_COLORS.training,
        show: true,
      },
      {
        label: lang === "dk" ? "Træningsrækker" : "Training rows",
        value: records.length,
        color: "#9dc1e8",
        show: true,
      },
    ],
    [lang]
  );

  const handleDownloadPdf = async () => {
    setPdfDownloading(true);
    try {
      await generateRiskAnalysisPdf({
        lang,
        orgName,
        dateFrom,
        dateTo,
        timeRange,
        setNumbers,
        employees,
        includeTrainingData,
        includeReadData,
        showReportingCategory,
        isPrototypeGrouped,
        incidentChartGroups,
        defaultPool,
        trainingPool,
        readPool,
        hasRiskReportingCategoryFilter,
        riskReportingTableRecords,
        actionCards,
        riskPdfTableRecords,
        mapRecordForPdf,
        incidentExportRefs,
        incidentReportingExportRefs,
        tPerformanceExportRefs,
        incidentRef,
        tPerformanceRef,
        buildRiskPdfSummaryRows,
        buildReportingPdfSummaryRows,
        buildPerformancePdfSummaryRows,
      });
    } catch (err) {
      console.error("PDF export failed", err);
      alert("Kunne ikke gemme PDF. Prøv igen.");
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleDownloadPng = async () => {
    setPngDownloading(true);
    try {
      const refs = incidentChartGroups
        .flatMap((group) => [
          {
            node: incidentImageExportRefs.current[group.key],
            name: buildImageFileName("real-incident", group),
          },
          ...(showReportingCategory && hasRiskReportingCategoryFilter
            ? [
                {
                  node: incidentReportingImageExportRefs.current[group.key],
                  name: buildImageFileName("reporting-category", group),
                },
              ]
            : []),
          {
            node: tPerformanceImageExportRefs.current[group.key],
            name: buildImageFileName("t-performance", group),
          },
        ])
        .filter((item) => item.node);

      if (!refs.length) {
        throw new Error("No export charts available.");
      }

      await new Promise((resolve) =>
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(resolve)
        )
      );

      const zip = new JSZip();
      for (const item of refs) {
        const dataUrl = await captureNodeAsPng(item.node);
        zip.file(item.name, dataUrl.split(",")[1], { base64: true });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `risk-analysis-images.zip`);
    } catch (err) {
      console.error("Image zip export failed", err);
      alert("Kunne ikke gemme billederne. Prøv igen.");
    } finally {
      setPngDownloading(false);
    }
  };

  const renderLoader = (label) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
      <CircularProgress size={18} />
      <Typography variant="body2">{label}</Typography>
    </Box>
  );

  const displayTableRecords =
    userRole === "employee" || userRole === "employe"
      ? tableRecords.filter((r) => String(r.user_id ?? "") === currentUserId)
      : tableRecords;

  const displayRiskReportingTableRecords =
    userRole === "employee" || userRole === "employe"
      ? riskReportingTableRecords.filter(
          (r) => String(r.user_id ?? "") === currentUserId
        )
      : riskReportingTableRecords;

  const displayReportingTableRecords =
    userRole === "employee" || userRole === "employe"
      ? reportingTableRecords.filter(
          (r) => String(r.user_id ?? "") === currentUserId
        )
      : reportingTableRecords;

  const riskPdfTableRecords = useMemo(
    () =>
      dedupeBy(
        [...displayTableRecords, ...displayRiskReportingTableRecords],
        (record) => getRiskRecordKey(record)
      ),
    [displayTableRecords, displayRiskReportingTableRecords]
  );

  const isRiskDownloadBusy = pdfDownloading || pngDownloading || csvDownloading;

  const actionCardLookup = useMemo(() => {
    const map = {};
    actionCards.forEach((card) => {
      map[String(card.ac_no)] = {
        en: card.english_short_name,
        dk: card.english_short_name,
      };
    });
    return map;
  }, [actionCards]);

  const mapRecordForPdf = useCallback(
    (rec) => {
      const [datePart, timePart] = (rec.utc_tid_skannet ?? "").split(" ");
      const lookup = actionCardLookup[String(rec.action_card_number)] ?? {};
      const cardLabel = lookup[lang] ?? rec.action_card_number ?? "-";

      return {
        date: datePart || "-",
        time: timePart || "-",
        set: rec.nb_set ?? "-",
        card: cardLabel,
        comment: rec.comment || "—",
        employee: rec.employee ?? "-",
      };
    },
    [actionCardLookup, lang]
  );

  const handleDownloadRiskCsv = async () => {
    setCsvDownloading(true);
    try {
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      downloadCsv(displayTableRecords, "risk_analysis.csv");
    } finally {
      setCsvDownloading(false);
    }
  };

  const handleDownloadReportingCsv = async () => {
    setCsvDownloading(true);
    try {
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      downloadCsv(displayReportingTableRecords, "reporting_category.csv");
    } finally {
      setCsvDownloading(false);
    }
  };

  const resetGraphFilters = useCallback(() => {
    setDateFrom(MIN_DATE);
    setDateTo(todayStr());
    setTimeRange(DEFAULT_TIME_RANGE);
    setEmployees([]);
    setSetNumbers([]);
    setShowMinInjuries(true);
    setShowRealIncident(true);
    setShowTraining(false);
    setShowRead(false);
    setShowNull(false);
    setShowIncidentYellow(false);
    setShowIncidentRed(false);

    setRDateFrom(threeMonthsAgoStr());
    setRDateTo(todayStr());
    setRTimeRange(DEFAULT_TIME_RANGE);
    setREmployees([]);
    setRSetNumbers([]);
    setRShowNearMiss(true);
    setRShowPrevention(true);
    setRShowDoubtAboutReporting(true);

    setMDateFrom(weekAgoStr());
    setMDateTo(todayStr());
    setMTimeRange(DEFAULT_TIME_RANGE);
    setMSetNumbers([]);
    setMShowGreen(true);
    setMShowYellow(true);
    setMShowRed(true);

    setGDateFrom(weekAgoStr());
    setGDateTo(todayStr());
    setGTimeRange(DEFAULT_TIME_RANGE);
    setGSetNumbers([]);

    setEDateFrom(MIN_DATE);
    setEDateTo(todayStr());
    setETimeRange(DEFAULT_TIME_RANGE);
    setESetNumbers([]);
    setEBeach("");
  }, []);

  // ── Build tab list dynamically based on org ──────────────────────────────
  const tabDefs = useMemo(() => {
    const tabs = [{ key: "faktuel", label: t("faktuel") }];
    if (showReportingCategory) {
      tabs.push({ key: "reportingCategory", label: t("reportingCategory") });
    }
    tabs.push({ key: "performance", label: t("performance") });
    if (showMissionGuest(organizationId)) {
      tabs.push({ key: "mission", label: t("mission") });
      tabs.push({ key: "guest", label: t("guest") });
    }
    if (showEstimated(organizationId)) {
      tabs.push({ key: "estimated", label: t("estimated") });
    }
    return tabs;
  }, [lang, organizationId, showReportingCategory]);

  useEffect(() => {
    if (tab >= tabDefs.length) {
      setTab(0);
    }
  }, [tab, tabDefs]);

  const activeTabKey = tabDefs[tab]?.key;

  const handleTabChange = useCallback(
    (nextTab) => {
      if (nextTab === tab) return;
      resetGraphFilters();
      setTab(nextTab);
    },
    [resetGraphFilters, tab]
  );

  return {
    organizationId,
    lang,
    setLang,
    tab,
    setTab: handleTabChange,
    tabDefs,
    activeTabKey,
    orgLoading,
    orgName,
    permissionsLoading,
    permissionsError,
    permissionsSuccess,
    riskRecordsLoading,
    riskRecordsError,
    riskRecordsSuccess,
    actionCardsLoading,
    actionCardsError,
    missionGuestLoading,
    missionGuestError,
    estimatedLoading,
    estimatedError,
    canDate,
    canTime,
    canSet,
    canEmployee,
    canDownload,
    showReportingCategory,
    t,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    timeRange,
    setTimeRange,
    allowedTimeRange,
    setNumbers,
    setSetNumbers,
    employees,
    setEmployees,
    employeeOptions,
    realIncidentSetOptions,
    showMinInjuries,
    setShowMinInjuries,
    showRealIncident,
    setShowRealIncident,
    showTraining,
    setShowTraining,
    showRead,
    setShowRead,
    showNull,
    setShowNull,
    showIncidentYellow,
    setShowIncidentYellow,
    showIncidentRed,
    setShowIncidentRed,
    rDateFrom,
    setRDateFrom,
    rDateTo,
    setRDateTo,
    rTimeRange,
    setRTimeRange,
    rSetNumbers,
    setRSetNumbers,
    rEmployees,
    setREmployees,
    rShowNearMiss,
    rShowPrevention,
    rShowDoubtAboutReporting,
    reportingCategorySetOptions,
    mDateFrom,
    setMDateFrom,
    mDateTo,
    setMDateTo,
    mTimeRange,
    setMTimeRange,
    mSetNumbers,
    setMSetNumbers,
    mShowGreen,
    setMShowGreen,
    mShowYellow,
    setMShowYellow,
    mShowRed,
    setMShowRed,
    gDateFrom,
    setGDateFrom,
    gDateTo,
    setGDateTo,
    gTimeRange,
    setGTimeRange,
    gSetNumbers,
    setGSetNumbers,
    eDateFrom,
    setEDateFrom,
    eDateTo,
    setEDateTo,
    eTimeRange,
    setETimeRange,
    eSetNumbers,
    setESetNumbers,
    eBeach,
    setEBeach,
    pdfDownloading,
    pngDownloading,
    csvDownloading,
    isRiskDownloadBusy,
    handleDownloadPdf,
    handleDownloadPng,
    handleDownloadRiskCsv,
    handleDownloadReportingCsv,
    downloadCsv,
    missionFiltered,
    guestFiltered,
    estimatedFiltered,
    estimatedData,
    missionSetOptions,
    estimatedSetOptions,
    incidentChartGroups,
    reportingPrototypeGroups,
    prototypeChartGroups,
    isPrototypeGrouped,
    incidentRef,
    tPerformanceRef,
    tableRef,
    missionRef,
    guestRef,
    estimatedRef,
    assignNodeRef,
    incidentChartRefs,
    tPerformanceChartRefs,
    incidentImageExportRefs,
    incidentReportingImageExportRefs,
    tPerformanceImageExportRefs,
    incidentExportRefs,
    incidentReportingExportRefs,
    tPerformanceExportRefs,
    missionExportRef,
    guestExportRef,
    estimatedExportRef,
    includeTrainingData,
    includeReadData,
    isGentofteLife,
    hasRiskReportingCategoryFilter,
    actionCards,
    riskPdfTableRecords,
    buildImageHeaderRows,
    buildRiskImageDetailRows,
    buildReportingImageDetailRows,
    buildPerformanceImageDetailRows,
    renderLoader,
  };
};
